import {LiveStageTransitionReason} from './stage-transition.js';

function required(value,label){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(label+' is required');
  return text;
}

function active(rows=[]){
  return (rows||[]).filter(row=>!row?.deletedAt)
    .sort((a,b)=>Number(a?.sortOrder||0)-Number(b?.sortOrder||0)||String(a?.id||'').localeCompare(String(b?.id||'')));
}

export function createLiveStageTransitionService({
  sessions,
  stages,
  transitions,
  events,
  conflictGuard=null,
  sessionId,
  now=()=>new Date().toISOString()
}={}){
  if(!sessions?.get||!sessions?.changeActiveStage)throw new TypeError('sessions store is required');
  if(!stages?.list||!stages?.createLiveCustom)throw new TypeError('stages store is required');
  if(!transitions?.append)throw new TypeError('transition store is required');
  if(!events?.append)throw new TypeError('event store is required');
  const session=required(sessionId,'sessionId');

  async function context(){
    const current=await sessions.get(session);
    if(!current)throw new Error('SMOKING_SESSION_NOT_FOUND');
    if(current.status!=='smoking')throw new Error('LIVE_STAGE_TRANSITION_REQUIRES_SMOKING');
    if(current.syncStatus==='conflict')throw new Error('LIVE_STAGE_SESSION_CONFLICT');
    if(await conflictGuard?.hasRejectedStageEvidence?.(session)){
      throw new Error('LIVE_STAGE_CONFLICT_UNRESOLVED');
    }
    const ordered=active(await stages.list(session));
    const index=ordered.findIndex(stage=>stage.id===current.activeStageId);
    if(index<0)throw new Error('LIVE_ACTIVE_STAGE_NOT_FOUND');
    return {current,ordered,index};
  }

  async function plan(toStageId){
    const targetId=required(toStageId,'toStageId');
    const {current,ordered,index}=await context();
    const targetIndex=ordered.findIndex(stage=>stage.id===targetId);
    if(targetIndex<0)throw new Error('LIVE_TARGET_STAGE_NOT_FOUND');
    if(targetIndex===index)throw new Error('LIVE_TARGET_STAGE_ALREADY_ACTIVE');

    let reason;
    let skipped=[];
    let confirmationRequired=false;
    if(targetIndex===index+1){
      reason=LiveStageTransitionReason.ADVANCE;
    }else if(targetIndex>index+1){
      reason=LiveStageTransitionReason.SKIP;
      skipped=ordered.slice(index+1,targetIndex).map(stage=>stage.id);
      confirmationRequired=true;
    }else{
      reason=LiveStageTransitionReason.REOPEN;
      confirmationRequired=true;
    }

    return Object.freeze({
      ok:false,
      status:confirmationRequired?'confirmation_required':'ready',
      confirmationRequired,
      reason,
      sessionId:session,
      fromStageId:current.activeStageId,
      toStageId:targetId,
      skippedStageIds:Object.freeze(skipped),
      fromStageStartedAt:current.activeStageStartedAt
    });
  }

  async function execute(planValue,{confirmed=false,reasonOverride=null}={}){
    const value=planValue||{};
    if(value.confirmationRequired&&!confirmed)return value;
    const at=now();
    const reason=reasonOverride||value.reason;

    const previous=await sessions.get(session);
    if(!previous||previous.status!=='smoking')throw new Error('LIVE_STAGE_TRANSITION_REQUIRES_SMOKING');
    if(previous.activeStageId!==value.fromStageId)throw new Error('LIVE_STAGE_TRANSITION_STALE');

    const changed=await sessions.changeActiveStage(session,{
      stageId:value.toStageId,
      at
    });

    try{
      const history=await transitions.append(session,{
        fromStageId:value.fromStageId,
        toStageId:value.toStageId,
        fromStageStartedAt:value.fromStageStartedAt,
        transitionedAt:at,
        reason,
        skippedStageIds:value.skippedStageIds||[],
        mutationBatchId:changed.stageMutationBatchId
      });
      await events.append(session,{
        eventType:'stage_changed',
        stageId:value.toStageId,
        occurredAt:at,
        stageMutationBatchId:changed.stageMutationBatchId,
        note:reason===LiveStageTransitionReason.SKIP
          ?'Pominięto etapy: '+(value.skippedStageIds||[]).length
          :reason===LiveStageTransitionReason.REOPEN
            ?'Ponownie otwarto wcześniejszy etap'
            :reason===LiveStageTransitionReason.CUSTOM_INSERT
              ?'Dodano własny etap podczas Live'
              :null
      });
      return Object.freeze({ok:true,status:'saved',session:changed,transition:history});
    }catch(error){
      try{
        await sessions.changeActiveStage(session,{
          stageId:previous.activeStageId,
          at:previous.activeStageStartedAt,
          mutationBatchId:previous.stageMutationBatchId
        });
      }catch{}
      throw error;
    }
  }

  async function transition(toStageId,{confirmed=false}={}){
    const value=await plan(toStageId);
    return execute(value,{confirmed});
  }

  async function insertCustomStage(name,{confirmed=true}={}){
    const {current}=await context();
    const created=await stages.createLiveCustom(session,{
      nameOverride:required(name,'nameOverride')
    },{sessionStatus:'smoking'});
    const value=Object.freeze({
      ok:false,
      status:'ready',
      confirmationRequired:false,
      reason:LiveStageTransitionReason.CUSTOM_INSERT,
      sessionId:session,
      fromStageId:current.activeStageId,
      toStageId:created.id,
      skippedStageIds:Object.freeze([]),
      fromStageStartedAt:current.activeStageStartedAt
    });
    return execute(value,{confirmed,reasonOverride:LiveStageTransitionReason.CUSTOM_INSERT});
  }

  return Object.freeze({plan,transition,insertCustomStage});
}
